import {
    Autocomplete,
    AutocompleteRenderInputParams,
    AutocompleteRenderOptionState,
    Checkbox,
    ListItemText,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {HTMLAttributes, useCallback, useMemo} from "react";
import {autocompleteChangeEvent, SetState} from "../../util/FormHandlers";
import {INeighbourhood} from "../../api/Model";
import {TextFieldProps} from "@mui/material/TextField/TextField";


export default function NeighbourhoodMultiselect({neighbourhoods, setNeighbourhoods, allNeighbourhoods, ...props}: {
    neighbourhoods: INeighbourhood[],
    setNeighbourhoods: SetState<INeighbourhood[]>,
    allNeighbourhoods: INeighbourhood[],
} & TextFieldProps) {
    const handleNeighbourhoodChange = useMemo(
        () => autocompleteChangeEvent<INeighbourhood[]>(neighbourhoods => setNeighbourhoods(neighbourhoods)),
        [setNeighbourhoods],
    );

    const renderNeighbourhoodsValue = useCallback(
        (values: INeighbourhood[]) => (
            <Stack direction="row" spacing={1} alignItems='center' flexWrap="nowrap" maxWidth="calc(100% - 40px)">
                <Typography variant='body2' sx={{
                    textWrap: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                }}>{values.length === 0 ? (
                    ''
                ) : values.length === 1 ? (
                    values[0].name
                ) : (
                    `${values.length} neighbourhoods`
                )}
                </Typography>
            </Stack>
        ),
        [],
    );

    const renderOption = useCallback(({key, ...props}: HTMLAttributes<HTMLLIElement> & {
        key: any
    }, option: INeighbourhood, {selected}: AutocompleteRenderOptionState) => (
        <li key={key} {...props}>
            <Checkbox checked={selected} style={{marginRight: 8}}/>
            <ListItemText primary={option.name}/>
        </li>
    ), []);

    const renderInput = useCallback((params: AutocompleteRenderInputParams) => (
        <TextField {...params} {...props}/>
    ), [props]);

    const isOptionEqualToValue = useCallback((option: INeighbourhood, value: INeighbourhood) => {
        return option.key === value.key;
    }, []);

    return (
        <Autocomplete
            multiple
            fullWidth
            disableCloseOnSelect
            options={allNeighbourhoods}
            value={neighbourhoods}
            onChange={handleNeighbourhoodChange}
            getOptionLabel={option => option.name}
            renderOption={renderOption}
            renderValue={renderNeighbourhoodsValue}
            renderInput={renderInput}
            isOptionEqualToValue={isOptionEqualToValue}
        />
    );
}
